import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Play, BarChart3, AlertCircle, CheckCircle2, Sparkles, TrendingUp, Database, Info } from 'lucide-react';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { safeLog } from '@/utils/safeLogger';

interface TestLog {
  id: string;
  sender_email: string | null;
  created_at: string;
  extraction_completeness: number | null;
}

interface GeminiLog {
  id: string;
  user_email: string | null;
  created_at: string;
  research_success: boolean | null;
}

interface PromptVersion {
  id: string;
  version_number: number;
  description?: string;
  prompt_type: string;
}

interface VersionPerformance {
  version_id: string;
  version_number: number;
  description: string | null;
  avg_completeness: number;
  field_accuracies: { [fieldName: string]: number };
  test_count: number;
}

export function ExperimentalTesting() {
  const [azureLogs, setAzureLogs] = useState<TestLog[]>([]);
  const [geminiLogs, setGeminiLogs] = useState<GeminiLog[]>([]);
  const [selectedAzureIds, setSelectedAzureIds] = useState<string[]>([]);
  const [selectedGeminiIds, setSelectedGeminiIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzingPrevious, setIsAnalyzingPrevious] = useState(false);
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  const [performanceData, setPerformanceData] = useState<VersionPerformance[]>([]);
  const [weaknessReport, setWeaknessReport] = useState<any>(null);
  const [optimizedPrompts, setOptimizedPrompts] = useState<any>(null);
  const [azureVersions, setAzureVersions] = useState<PromptVersion[]>([]);
  const [geminiVersions, setGeminiVersions] = useState<PromptVersion[]>([]);
  const [selectedGeminiVersion, setSelectedGeminiVersion] = useState<string>('production');
  const [selectedAzurePromptVersion, setSelectedAzurePromptVersion] = useState<string>('production');
  const [selectedGeminiPromptVersion, setSelectedGeminiPromptVersion] = useState<string>('production');
  const [isLoadingPerformance, setIsLoadingPerformance] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{
    processed: number;
    total: number;
    successful: number;
    failed: number;
    percentage: number;
  } | null>(null);
  const [lastAnalyzedLogIds, setLastAnalyzedLogIds] = useState<string[]>([]);

  useEffect(() => {
    loadAzureLogs();
    loadGeminiLogs();
    loadPromptVersions();
    loadLastBatchSession();
  }, []);

  const loadAzureLogs = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('signature_extraction_logs')
        .select('id, sender_email, created_at, extraction_completeness, run_id')
        .order('created_at', { ascending: false })
        .limit(100);

    if (error) throw error;
    setAzureLogs(data || []);
    } catch (error: any) {
      safeLog.error('[Experimental] Load Azure logs error', error as Error);
      toast.error('Failed to load Azure logs');
    } finally {
      setIsLoading(false);
    }
  };

  const loadGeminiLogs = async () => {
    try {
    const { data, error } = await supabase
      .from('smart_notes_logs')
      .select('id, created_at, success')
      .order('created_at', { ascending: false })
      .limit(100);

      if (error) throw error;
    setGeminiLogs((data || []).map((log: any) => ({ 
      id: log.id, 
      user_email: null, 
      created_at: log.created_at, 
      research_success: log.success 
    })));
    } catch (error: any) {
      safeLog.error('[Experimental] Load Gemini logs error', error as Error);
      toast.error('Failed to load Gemini logs');
    }
  };

  const loadPromptVersions = async () => {
    try {
      const { data, error } = await supabase
        .from('prompt_versions')
        .select('id, version_number, description, prompt_type')
        .order('version_number', { ascending: false });

      if (error) throw error;

      const azure = (data || []).filter(v => v.prompt_type === 'azure_extraction');
      const gemini = (data || []).filter(v => v.prompt_type === 'gemini_research');

      setAzureVersions(azure);
      setGeminiVersions(gemini);
    } catch (error: any) {
      safeLog.error('[Experimental] Load versions error', error as Error);
    }
  };

  const loadLastBatchSession = async () => {
    try {
      // Find the most recent test_extraction_results
      const { data: recentTests, error } = await supabase
        .from('test_extraction_results')
        .select('original_log_id, created_at')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      if (!recentTests || recentTests.length === 0) return;

      // Find the latest "batch session" (logs with similar timestamp)
      const latestTimestamp = new Date(recentTests[0].created_at);
      const sessionWindow = new Date(latestTimestamp.getTime() - 10 * 60 * 1000); // 10 minute window

      const sessionLogIds = recentTests
        .filter(t => new Date(t.created_at) >= sessionWindow)
        .map(t => t.original_log_id)
        .filter((id, index, self) => id && self.indexOf(id) === index) as string[]; // unique

      if (sessionLogIds.length > 0) {
        safeLog.info(`[Experimental] Restored last batch session: ${sessionLogIds.length} logs`);
        setLastAnalyzedLogIds(sessionLogIds);
        
        // Auto-load Performance Comparison
        await loadPerformanceComparison(sessionLogIds);
      }
    } catch (error) {
      safeLog.error('[Experimental] Failed to restore last session', error as Error);
    }
  };

  // Generate distinct colors for chart versions using HSL color space
  const generateVersionColors = (count: number): string[] => {
    const colors: string[] = [];
    const saturation = 70; // Fixed saturation for consistency
    const lightness = 55;  // Fixed lightness for readability
    
    for (let i = 0; i < count; i++) {
      // Distribute hue evenly across color spectrum (0-360 degrees)
      const hue = Math.floor((360 / count) * i);
      colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
    }
    
    return colors;
  };

  const toggleAzureSelection = (logId: string) => {
    setSelectedAzureIds(prev =>
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    );
  };

  const toggleGeminiSelection = (logId: string) => {
    setSelectedGeminiIds(prev =>
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    );
  };

  const selectAllAzure = () => {
    setSelectedAzureIds(azureLogs.map(log => log.id));
    toast.success(`Selected all ${azureLogs.length} Azure logs`);
  };

  const deselectAllAzure = () => {
    setSelectedAzureIds([]);
    toast.info('All Azure logs deselected');
  };

  const selectAllGemini = () => {
    setSelectedGeminiIds(geminiLogs.map(log => log.id));
    toast.success(`Selected all ${geminiLogs.length} Gemini logs`);
  };

  const deselectAllGemini = () => {
    setSelectedGeminiIds([]);
    toast.info('All Gemini logs deselected');
  };

  const analyzePreviousBatch = async () => {
    const logIdsToAnalyze = lastAnalyzedLogIds.length > 0 ? lastAnalyzedLogIds : selectedAzureIds;
    
    if (logIdsToAnalyze.length === 0) {
      toast.error('Please select logs or run a batch analysis first');
      return;
    }

    setIsAnalyzingPrevious(true);
    const analysisId = crypto.randomUUID();
    
    try {
      safeLog.info(`[Experimental] Starting batch analysis for ${logIdsToAnalyze.length} logs`);

      const { data, error } = await supabase.functions.invoke('batch-analyze-logs', {
        body: {
          log_ids: logIdsToAnalyze,
          analysis_id: analysisId,
          azure_prompt_version_id: selectedAzurePromptVersion === 'production' ? null : selectedAzurePromptVersion,
          gemini_prompt_version_id: null,
          skip_gemini: true
        }
      });

      if (error) throw error;

      const batchId = data.batch_id;
      toast.info(`🧪 Batch analysis started. Processing ${data.total_logs} selected logs...`);

      const checkInterval = setInterval(async () => {
        try {
          const { data: status, error: statusError } = await supabase.functions.invoke('get-batch-status', {
            body: { batch_id: batchId }
          });

          if (statusError) {
            safeLog.error('Error checking batch status', statusError as Error);
            clearInterval(checkInterval);
            toast.error('Failed to check batch status');
            setIsAnalyzingPrevious(false);
            return;
          }

          safeLog.info(`[Batch Progress] ${status.processed_logs}/${status.total_logs} logs (${status.successful_logs} successful, ${status.failed_logs} failed)`);

          if (status.status === 'completed') {
            clearInterval(checkInterval);
            toast.success(`✅ Analysis complete! Processed ${status.total_logs} logs (${status.successful_logs} successful, ${status.failed_logs} failed)`);
            await loadPerformanceComparison(logIdsToAnalyze);
            setIsAnalyzingPrevious(false);
          } else if (status.status === 'failed') {
            clearInterval(checkInterval);
            toast.error(`❌ Analysis failed: ${status.error_message}`);
            setIsAnalyzingPrevious(false);
          }
        } catch (error) {
          safeLog.error('Error in polling', error as Error);
          clearInterval(checkInterval);
          toast.error('Error monitoring batch status');
          setIsAnalyzingPrevious(false);
        }
      }, 5000);

    } catch (error: any) {
      safeLog.error('[Experimental] Batch analysis error', error as Error);
      toast.error('Failed to start batch analysis');
      setIsAnalyzingPrevious(false);
    }
  };

  const analyzeAllLogs = async () => {
    if (azureLogs.length === 0) {
      toast.error('No logs available to analyze');
      return;
    }

    setIsAnalyzingAll(true);
    const analysisId = crypto.randomUUID();
    
    try {
      const allLogIds = azureLogs.map(log => log.id);
      setLastAnalyzedLogIds(allLogIds);
      safeLog.info(`[Experimental] Starting batch analysis for all ${allLogIds.length} loaded logs`);

      const { data, error } = await supabase.functions.invoke('batch-analyze-logs', {
        body: {
          log_ids: allLogIds,
          analysis_id: analysisId,
          azure_prompt_version_id: selectedAzurePromptVersion === 'production' ? null : selectedAzurePromptVersion,
          gemini_prompt_version_id: null,
          skip_gemini: true
        }
      });

      if (error) throw error;

      const batchId = data.batch_id;
      toast.info(`🧪 Batch analysis started. Processing all ${data.total_logs} loaded logs...`);

      const checkInterval = setInterval(async () => {
        try {
          const { data: status, error: statusError } = await supabase.functions.invoke('get-batch-status', {
            body: { batch_id: batchId }
          });

          if (statusError) {
            safeLog.error('Error checking batch status', statusError as Error);
            clearInterval(checkInterval);
            toast.error('Failed to check batch status');
            setIsAnalyzingAll(false);
            setBatchProgress(null);
            return;
          }

          // Update progress state for UI
          const percentage = status.total_logs > 0 
            ? Math.round((status.processed_logs / status.total_logs) * 100) 
            : 0;
          
          setBatchProgress({
            processed: status.processed_logs,
            total: status.total_logs,
            successful: status.successful_logs,
            failed: status.failed_logs,
            percentage
          });

          safeLog.info(`[Batch Progress] ${status.processed_logs}/${status.total_logs} (${percentage}%) - ${status.successful_logs} ok, ${status.failed_logs} failed`);

          if (status.status === 'completed') {
            clearInterval(checkInterval);
            toast.success(`✅ Analysis complete! Processed ${status.total_logs} logs (${status.successful_logs} successful, ${status.failed_logs} failed)`);
            await loadPerformanceComparison(allLogIds);
            setIsAnalyzingAll(false);
            setBatchProgress(null);
          } else if (status.status === 'failed') {
            clearInterval(checkInterval);
            toast.error(`❌ Analysis failed: ${status.error_message}`);
            setIsAnalyzingAll(false);
            setBatchProgress(null);
          }
        } catch (error) {
          safeLog.error('Error in polling', error as Error);
          clearInterval(checkInterval);
          toast.error('Error monitoring batch status');
          setIsAnalyzingAll(false);
          setBatchProgress(null);
        }
      }, 2000);

    } catch (error: any) {
      safeLog.error('[Experimental] Analyze all logs error', error as Error);
      toast.error('Failed to start batch analysis');
      setIsAnalyzingAll(false);
    }
  };

  const analyzeGeminiPreviousBatch = async () => {
    setIsAnalyzingPrevious(true);
    const analysisId = crypto.randomUUID();
    
    try {
      // Get recent Gemini logs (last 50)
      const { data: recentGeminiLogs, error } = await supabase
        .from('smart_notes_logs')
        .select('id')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      
      if (!recentGeminiLogs || recentGeminiLogs.length === 0) {
        toast.error('No Gemini logs found. Enable Smart Notes and extract some contacts first.');
        return;
      }

      toast.info(`🧪 Testing Gemini on ${recentGeminiLogs.length} recent logs...`);

      let successCount = 0;
      let errorCount = 0;

      for (const log of recentGeminiLogs) {
        const { data, error: testError } = await supabase.functions.invoke('test-gemini-prompt', {
          body: {
            log_id: log.id,
            prompt_version_id: selectedGeminiPromptVersion === 'production' ? undefined : selectedGeminiPromptVersion,
            analysis_id: analysisId
          }
        });

        if (testError || !data?.success) {
          errorCount++;
        } else {
          successCount++;
        }
      }

      toast.success(`✅ Tested ${successCount} Gemini logs (${errorCount} errors)`);
    } catch (error: any) {
      safeLog.error('[Experimental] Gemini batch analysis error', error as Error);
      toast.error('Failed to analyze Gemini batch: ' + error.message);
    } finally {
      setIsAnalyzingPrevious(false);
    }
  };

  const analyzeGeminiAllLogs = async () => {
    setIsAnalyzingAll(true);
    const analysisId = crypto.randomUUID();
    
    try {
      // Get all Gemini logs
      const { data: allGeminiLogs, error } = await supabase
        .from('smart_notes_logs')
        .select('id')
        .order('created_at', { ascending: false })
        .limit(500);
      
      if (error) throw error;
      
      if (!allGeminiLogs || allGeminiLogs.length === 0) {
        toast.error('No Gemini logs found');
        return;
      }

      toast.info(`🧪 Testing Gemini on ${allGeminiLogs.length} logs...`);

      let successCount = 0;
      let errorCount = 0;

      for (const log of allGeminiLogs) {
        const { data, error: testError } = await supabase.functions.invoke('test-gemini-prompt', {
          body: {
            log_id: log.id,
            prompt_version_id: selectedGeminiPromptVersion === 'production' ? undefined : selectedGeminiPromptVersion,
            analysis_id: analysisId
          }
        });

        if (testError || !data?.success) {
          errorCount++;
        } else {
          successCount++;
        }
      }

      toast.success(`✅ Tested ${successCount} Gemini logs (${errorCount} errors)`);
    } catch (error: any) {
      safeLog.error('[Experimental] Analyze all Gemini logs error', error as Error);
      toast.error('Failed to analyze all Gemini logs');
    } finally {
      setIsAnalyzingAll(false);
    }
  };

  const runAzureTextOnlyAnalysis = async (promptVersionId?: string) => {
    if (selectedAzureIds.length === 0) {
      toast.error('Please select at least one log');
      return;
    }

    setIsAnalyzingPrevious(true);
    toast.info(`🧪 Running text-only analysis on ${selectedAzureIds.length} logs...`);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const logId of selectedAzureIds) {
        const { data, error } = await supabase.functions.invoke('analyze-signature-text-only', {
          body: {
            log_id: logId,
            model: 'azure',
            prompt_version_id: promptVersionId,
          },
        });

        if (error || !data.success) {
          errorCount++;
          safeLog.error(`[Experimental] Analysis failed for ${logId}`, error as Error);
        } else {
          successCount++;
        }
      }

      toast.success(`✅ Analyzed ${successCount} logs (${errorCount} errors)`);
      await loadPerformanceComparison();
    } catch (error: any) {
      safeLog.error('[Experimental] Analysis error', error as Error);
      toast.error('Text-only analysis failed');
    } finally {
      setIsAnalyzingPrevious(false);
    }
  };

  const runGeminiPromptTest = async () => {
    if (selectedGeminiIds.length === 0) {
      toast.error('Please select at least one Gemini log');
      return;
    }

    setIsAnalyzingPrevious(true);
    toast.info(`🧪 Testing Gemini prompt on ${selectedGeminiIds.length} logs...`);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const logId of selectedGeminiIds) {
        const { data, error } = await supabase.functions.invoke('test-gemini-prompt', {
          body: {
            log_id: logId,
            prompt_version_id: selectedGeminiVersion === "production" ? undefined : selectedGeminiVersion,
          },
        });

        if (error || !data.success) {
          errorCount++;
          safeLog.error(`[Experimental] Gemini test failed for ${logId}`, error as Error);
        } else {
          successCount++;
        }
      }

      toast.success(`✅ Tested ${successCount} Gemini logs (${errorCount} errors)`);
    } catch (error: any) {
      safeLog.error('[Experimental] Gemini test error', error as Error);
      toast.error('Gemini prompt test failed');
    } finally {
      setIsAnalyzingPrevious(false);
    }
  };

  const loadPerformanceComparison = async (explicitLogIds?: string[]) => {
    setIsLoadingPerformance(true);
    try {
      const logIdsToLoad = explicitLogIds || 
                           (lastAnalyzedLogIds.length > 0 ? lastAnalyzedLogIds : selectedAzureIds);
      
      if (logIdsToLoad.length === 0) {
        toast.info('No logs to load. Select logs or run batch analysis first.');
        setPerformanceData([]);
        setIsLoadingPerformance(false);
        return;
      }
      
      safeLog.info(`[Performance] Loading results for ${logIdsToLoad.length} logs`);

      const { data: testResults, error: testError } = await supabase
        .from('test_extraction_results')
        .select('*, prompt_versions(version_number, description)')
        .in('original_log_id', logIdsToLoad)
        .order('created_at', { ascending: false });

      if (testError) throw testError;

      if (!testResults || testResults.length === 0) {
        toast.info(`No test results found for ${logIdsToLoad.length} logs. They may still be processing.`);
        setPerformanceData([]);
        setIsLoadingPerformance(false);
        return;
      }
      
      safeLog.info(`[Performance] Found ${testResults.length} test results`);

      // Group by prompt version
      const versionMap: { [versionId: string]: {
        version_number: number;
        description: string | null;
        results: any[];
      } } = {};

      testResults.forEach((result: any) => {
        if (result.prompt_version_id) {
          if (!versionMap[result.prompt_version_id]) {
            versionMap[result.prompt_version_id] = {
              version_number: result.prompt_versions?.version_number || 0,
              description: result.prompt_versions?.description || null,
              results: []
            };
          }
          versionMap[result.prompt_version_id].results.push(result);
        }
      });

      // Calculate stats per version
      const versionPerformances: VersionPerformance[] = Object.entries(versionMap).map(([versionId, versionData]) => {
        const fieldStats: { [key: string]: { total: number; count: number } } = {};
        let totalCompleteness = 0;

        versionData.results.forEach((result: any) => {
          if (result.extraction_completeness) {
            totalCompleteness += result.extraction_completeness;
          }
          
          if (result.extracted_data) {
            Object.keys(result.extracted_data).forEach(field => {
              if (!fieldStats[field]) {
                fieldStats[field] = { total: 0, count: 0 };
              }
              if (result.extracted_data[field]) {
                fieldStats[field].total += 1;
              }
              fieldStats[field].count += 1;
            });
          }
        });

        const fieldAccuracies: { [fieldName: string]: number } = {};
        Object.entries(fieldStats).forEach(([field, stats]) => {
          fieldAccuracies[field] = stats.count > 0 ? (stats.total / stats.count) * 100 : 0;
        });

        return {
          version_id: versionId,
          version_number: versionData.version_number,
          description: versionData.description,
          avg_completeness: versionData.results.length > 0 ? totalCompleteness / versionData.results.length : 0,
          field_accuracies: fieldAccuracies,
          test_count: versionData.results.length
        };
      });

      // Sort by version number descending
      versionPerformances.sort((a, b) => b.version_number - a.version_number);

      setPerformanceData(versionPerformances);
      toast.success('Performance comparison loaded');
    } catch (error: any) {
      safeLog.error('[Experimental] Performance comparison error', error as Error);
      toast.error('Failed to load performance comparison');
    } finally {
      setIsLoadingPerformance(false);
    }
  };

  const generateWeaknessReport = async () => {
    const logIdsToAnalyze = lastAnalyzedLogIds.length > 0 ? lastAnalyzedLogIds : selectedAzureIds;
    
    if (logIdsToAnalyze.length === 0) {
      toast.error('Please run batch analysis or select logs first');
      return;
    }

    setIsLoading(true);
    toast.info(`🔍 Analyzing weakness patterns for ${logIdsToAnalyze.length} logs with Azure AI...`);

    try {
      const { data, error } = await supabase.functions.invoke('generate-weakness-report', {
        body: { log_ids: logIdsToAnalyze },
      });

      if (error) throw error;

      setWeaknessReport(data.weakness_report);
      toast.success(`✅ Weakness report generated from ${data.logs_analyzed} logs`);
      
      // ✅ Performance Comparison automatisch aktualisieren
      await loadPerformanceComparison(logIdsToAnalyze);
      
    } catch (error: any) {
      safeLog.error('[Experimental] Weakness report error', error as Error);
      toast.error('Failed to generate weakness report');
    } finally {
      setIsLoading(false);
    }
  };

  const optimizePrompts = async () => {
    if (!weaknessReport) {
      toast.error('Please generate a weakness report first');
      return;
    }

    setIsLoading(true);
    const logCount = lastAnalyzedLogIds.length > 0 ? lastAnalyzedLogIds.length : selectedAzureIds.length;
    toast.info(`✨ Optimizing prompts based on ${logCount} logs with AI...`);

    try {
      const { data, error } = await supabase.functions.invoke('optimize-prompt-with-weaknesses', {
        body: { weakness_report: weaknessReport },
      });

      if (error) throw error;

      setOptimizedPrompts(data);
      toast.success('✅ Optimized prompts generated');
    } catch (error: any) {
      safeLog.error('[Experimental] Optimize prompts error', error as Error);
      toast.error('Failed to optimize prompts');
    } finally {
      setIsLoading(false);
    }
  };

  const testOptimizedPrompt = async () => {
    if (!optimizedPrompts) {
      toast.error('Please generate optimized prompts first');
      return;
    }

    const logIdsToTest = lastAnalyzedLogIds.length > 0 ? lastAnalyzedLogIds : selectedAzureIds;
    
    if (logIdsToTest.length === 0) {
      toast.error('No logs to test with. Run batch analysis first.');
      return;
    }

    setIsLoading(true);
    toast.info(`🧪 Testing optimized prompt on ${logIdsToTest.length} original logs...`);

    try {
      // Direkt mit optimierten Prompts testen (KEINE Version erstellen!)
      const { data: reextractionData, error: reextractionError } = await supabase.functions.invoke(
        'reextract-with-new-prompt',
        {
          body: {
            logIds: logIdsToTest,
            system_prompt: optimizedPrompts.optimized_system_prompt,
            user_prompt_template: optimizedPrompts.optimized_user_prompt_template,
          },
        }
      );

      if (reextractionError) throw reextractionError;

      // Performance Comparison aktualisieren
      await loadPerformanceComparison(logIdsToTest);

      // Success-Nachricht mit Statistik
      const improvementSign = reextractionData.avgImprovement * 100 >= 0 ? '+' : '';
      toast.success(
        `✅ Test complete!\nAvg Old Completeness: ${(reextractionData.avgOldCompleteness * 100).toFixed(1)}%\nAvg New Completeness: ${(reextractionData.avgNewCompleteness * 100).toFixed(1)}%\nImprovement: ${improvementSign}${(reextractionData.avgImprovement * 100).toFixed(1)}%`,
        { duration: 8000 }
      );

      toast.info('💡 Results are temporary. Use "Save as New Version" to persist the prompts.');

      // Azureversions neu laden (falls welche im Hintergrund erstellt wurden)
      const { data: versions } = await supabase
        .from('prompt_versions')
        .select('*')
        .eq('prompt_type', 'azure_extraction')
        .order('version_number', { ascending: true });
      
      if (versions) setAzureVersions(versions);

    } catch (error: any) {
      safeLog.error('[Experimental] Test optimized prompt error', error as Error);
      toast.error('Failed to test optimized prompt');
    } finally {
      setIsLoading(false);
    }
  };

  const saveNewPromptVersion = async () => {
    if (!optimizedPrompts) return;

    setIsLoading(true);
    try {
      const maxVersion = Math.max(...azureVersions.map(v => v.version_number), 0);

      const { error } = await supabase
        .from('prompt_versions')
        .insert([{
          version_number: maxVersion + 1,
          prompt_type: 'azure_extraction',
          description: `AI-Optimized v${maxVersion + 1}`,
          system_prompt: optimizedPrompts.optimized_system_prompt,
          user_prompt_template: optimizedPrompts.optimized_user_prompt_template,
          parent_version_id: optimizedPrompts.parent_version_id,
          is_active_for_prod: false,
          optimization_notes: {
            changes: optimizedPrompts.changes_made,
            expected_improvement: optimizedPrompts.expected_improvement,
            source: 'experimental_testing',
          },
        }]);

      if (error) throw error;

      toast.success('✅ New prompt version saved');
      loadPromptVersions();
      setOptimizedPrompts(null);
    } catch (error: any) {
      safeLog.error('[Experimental] Save version error', error as Error);
      toast.error('Failed to save prompt version');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Tabs defaultValue="azure" className="space-y-6">
      <TabsList className="grid w-full max-w-md grid-cols-2">
        <TabsTrigger value="azure">
          <Database className="h-4 w-4 mr-2" />
          Azure Extraction
        </TabsTrigger>
        <TabsTrigger value="gemini">
          <Sparkles className="h-4 w-4 mr-2" />
          Gemini Research
        </TabsTrigger>
      </TabsList>

      <TabsContent value="azure" className="space-y-4">
        
        <Card>
          <CardHeader>
            <CardTitle>Batch Analysis (Azure)</CardTitle>
            <CardDescription>Test Azure extraction prompts on batches</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Label>Azure Prompt Version:</Label>
              <Select value={selectedAzurePromptVersion} onValueChange={setSelectedAzurePromptVersion}>
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="Production (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">Production (Active)</SelectItem>
                  {azureVersions.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      v{v.version_number} - {v.description || 'No description'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={analyzePreviousBatch} 
                disabled={isAnalyzingPrevious || isAnalyzingAll || (selectedAzureIds.length === 0 && lastAnalyzedLogIds.length === 0)}
                variant="default"
              >
                {isAnalyzingPrevious ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing {lastAnalyzedLogIds.length > 0 ? lastAnalyzedLogIds.length : selectedAzureIds.length} logs...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Analyze {lastAnalyzedLogIds.length > 0 ? 'Last Batch' : 'Selected'} ({lastAnalyzedLogIds.length > 0 ? lastAnalyzedLogIds.length : selectedAzureIds.length})
                  </>
                )}
              </Button>
              
              {/* Progress Bar during analysis */}
              {batchProgress && (
                <div className="space-y-2 p-4 bg-muted/50 rounded-lg border">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium">
                      Processing {batchProgress.processed}/{batchProgress.total} logs
                    </span>
                    <span className="text-muted-foreground">
                      {batchProgress.percentage}%
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300 ease-out"
                      style={{ width: `${batchProgress.percentage}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>✅ {batchProgress.successful} successful</span>
                    {batchProgress.failed > 0 && (
                      <span className="text-destructive">❌ {batchProgress.failed} failed</span>
                    )}
                  </div>
                </div>
              )}

              <Button 
                onClick={analyzeAllLogs} 
                disabled={isAnalyzingPrevious || isAnalyzingAll || azureLogs.length === 0}
                variant="outline"
                className="w-full"
              >
                {isAnalyzingAll ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {batchProgress 
                      ? `Processing ${batchProgress.percentage}%...`
                      : 'Starting analysis...'}
                  </>
                ) : (
                  <>
                    <Database className="h-4 w-4 mr-2" />
                    Analyze All Loaded ({azureLogs.length})
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Performance Comparison (Azure)
              {isLoadingPerformance && <Loader2 className="h-4 w-4 animate-spin" />}
            </CardTitle>
            <CardDescription>Compare field accuracy across prompt versions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {performanceData && performanceData.length > 0 && (() => {
              // Transform data: group by field instead of by version
              const allFields = new Set<string>();
              performanceData.forEach(versionData => {
                Object.keys(versionData.field_accuracies || {}).forEach(field => allFields.add(field));
              });

              const fieldComparisonData = Array.from(allFields).map(field => {
                const fieldData: any = { 
                  field: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                };
                
                performanceData.forEach(versionData => {
                  fieldData[`V${versionData.version_number}`] = versionData.field_accuracies?.[field] || 0;
                });
                
                return fieldData;
              });

              // Generate dynamic colors and create chart config
              const versionColors = generateVersionColors(performanceData.length);
              const chartConfig = performanceData.reduce((config, versionData, index) => {
                config[`V${versionData.version_number}`] = {
                  label: `Version ${versionData.version_number}`,
                  color: versionColors[index]
                };
                return config;
              }, {} as any);

              return (
                <ChartContainer config={chartConfig} className="h-[500px] w-full">
                  <BarChart data={fieldComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="field" 
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      interval={0}
                    />
                    <YAxis domain={[0, 100]} label={{ value: 'Accuracy (%)', angle: -90, position: 'insideLeft' }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    {performanceData.map((versionData, index) => (
                      <Bar 
                        key={`V${versionData.version_number}`}
                        dataKey={`V${versionData.version_number}`}
                        fill={versionColors[index]}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>
              );
            })()}

            {performanceData && performanceData.length === 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No test results found. Run batch analysis first to generate data.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Weakness Report (Azure)</CardTitle>
            <CardDescription>AI analyzes patterns in failed extractions</CardDescription>
          </CardHeader>
          <CardContent>
                  <Button 
                    onClick={generateWeaknessReport} 
                    disabled={isLoading || (selectedAzureIds.length === 0 && lastAnalyzedLogIds.length === 0)}
                  >
                    {isLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</>
                    ) : (
                      <><BarChart3 className="h-4 w-4 mr-2" />
                        Generate Weakness Report
                        {lastAnalyzedLogIds.length > 0 && ` (${lastAnalyzedLogIds.length} logs)`}
                      </>
                    )}
                  </Button>

            {weaknessReport && (
              <div className="mt-4 space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Top Weakness Patterns</AlertTitle>
                  <AlertDescription>
                    {weaknessReport.top_weakness_patterns?.map((pattern: any, idx: number) => (
                      <div key={idx} className="mt-2">
                        <Badge variant="destructive" className="mr-2">{pattern.frequency}x</Badge>
                        {pattern.pattern}
                      </div>
                    ))}
                  </AlertDescription>
                </Alert>

                {weaknessReport.recommendations && (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>Recommendations</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc pl-6 mt-2 space-y-1">
                        {weaknessReport.recommendations.map((rec: string, idx: number) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Optimize Azure Prompt</CardTitle>
            <CardDescription>AI generates improved prompts based on weaknesses</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={optimizePrompts} 
              disabled={isLoading || !weaknessReport}
            >
              {isLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Optimizing...</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" />Generate Optimized Prompt</>
              )}
            </Button>

            {optimizedPrompts && (
              <div className="mt-4 space-y-4">
                <Alert>
                  <TrendingUp className="h-4 w-4" />
                  <AlertTitle>Expected Improvement</AlertTitle>
                  <AlertDescription>{optimizedPrompts.expected_improvement}</AlertDescription>
                </Alert>

                <div className="border p-4 rounded">
                  <h4 className="font-semibold mb-2">Changes Made:</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    {optimizedPrompts.changes_made?.map((change: string, idx: number) => (
                      <li key={idx} className="text-sm">{change}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-2">
                  <Button onClick={testOptimizedPrompt} disabled={isLoading} variant="default">
                    {isLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing...</>
                    ) : (
                      <>🧪 Test Optimized Prompt (Temporary)</>
                    )}
                  </Button>
                  
                  <Button onClick={saveNewPromptVersion} disabled={isLoading} variant="outline">
                    {isLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      <>💾 Save as New Version</>
                    )}
                  </Button>
                </div>

                <Alert className="mt-4">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    💡 <strong>Test (Temporary):</strong> Tests the optimized prompt without creating a database version. Results are visible in the chart but won't persist after reload.
                    <br />
                    <strong>Save as New Version:</strong> Creates a permanent version in the database that can be activated for production.
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="gemini" className="space-y-4">
        
        <Card>
          <CardHeader>
            <CardTitle>Batch Analysis (Gemini)</CardTitle>
            <CardDescription>Test Gemini research prompts on batches</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Label>Gemini Prompt Version:</Label>
              <Select value={selectedGeminiPromptVersion} onValueChange={setSelectedGeminiPromptVersion}>
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="Production (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">Production (Active)</SelectItem>
                  {geminiVersions.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      v{v.version_number} - {v.description || 'No description'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={analyzeGeminiPreviousBatch} 
                disabled={isAnalyzingPrevious || isAnalyzingAll || selectedGeminiIds.length === 0}
                variant="default"
              >
                {isAnalyzingPrevious ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing {selectedGeminiIds.length} logs...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Analyze Selected ({selectedGeminiIds.length})
                  </>
                )}
              </Button>
              
              <Button 
                onClick={analyzeGeminiAllLogs} 
                disabled={isAnalyzingPrevious || isAnalyzingAll || geminiLogs.length === 0}
                variant="outline"
              >
                {isAnalyzingAll ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing all loaded logs...
                  </>
                ) : (
                  <>
                    <Database className="h-4 w-4 mr-2" />
                    Analyze All Loaded ({geminiLogs.length})
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Performance Comparison (Gemini)</CardTitle>
            <CardDescription>Compare Gemini prompt performance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Gemini performance tracking coming soon. Run batch analysis to test prompts.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Weakness Report (Gemini)</CardTitle>
            <CardDescription>AI analyzes patterns in failed Gemini research</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Gemini weakness analysis coming soon. Run batch tests to collect data.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Optimize Gemini Prompt</CardTitle>
            <CardDescription>AI generates improved Gemini prompts</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Gemini prompt optimization coming soon. Generate weakness reports first.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
